import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  IconButton,
  Skeleton,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useChat } from "../../../context";
import { useMessageSearch } from "../../../hooks";
import {
  getMessageTimestamp,
  MessageType,
  SearchSidebarProps,
} from "../../../interfaces/all";
import { formatMessageTime } from "../../../utils/date";
import { MessageSkeleton } from "../../shared/SkeletonLoader";
import Header from "../Header/Header";
import { SearchInput } from "./styles";

const SidebarContainer = styled(Box)({
  width: "100%",
  height: "100%",
  backgroundColor: "#ffffff",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
});

const CloseButton = styled(IconButton)({
  padding: "4px",
  color: "#54656f",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const Title = styled(Typography)({
  fontSize: "16px",
  fontWeight: 500,
  color: "#111b21",
});

const SearchContainer = styled(Box)({
  padding: "8px 16px",
  borderBottom: "1px solid #e9edef",
  backgroundColor: "#ffffff",
  width: "100%",
  boxSizing: "border-box",
});

const SearchInputWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#f0f2f5",
  borderRadius: "8px",
  padding: "4px 8px",
  width: "100%",
});

const SearchControls = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "4px 16px",
  borderBottom: "1px solid #e9edef",
  backgroundColor: "#f0f2f5",
});

const NavigationButton = styled(IconButton)({
  padding: "4px",
  color: "#54656f",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "&:disabled": {
    color: "#bdbdbd",
  },
});

const ResultCount = styled(Typography)({
  fontSize: "13px",
  color: "#54656f",
  margin: "0 8px",
});

const MessageList = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "8px 0",
  width: "100%",
});

const MessageItem = styled(Box)<{ selected?: boolean }>(({ selected }) => ({
  padding: "8px 16px",
  cursor: "pointer",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: selected ? "#e9edef" : "transparent",
  "&:hover": {
    backgroundColor: selected ? "#e9edef" : "#f5f6f6",
  },
  "& mark": {
    backgroundColor: "#ffeb3b",
    padding: "0 2px",
    borderRadius: "2px",
  },
}));

const MessageText = styled(Typography)({
  fontSize: "14px",
  color: "#111b21",
  marginBottom: "4px",
});

const MessageInfo = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "12px",
  color: "#667781",
});

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",
  width: "100%",
});

const SearchSidebar: React.FC<SearchSidebarProps> = ({ isOpen, onClose }) => {
  const { selectedConversation } = useChat();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Simular loading inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 800);

    // Cleanup para evitar race condition se componente for desmontado
    return () => {
      clearTimeout(timer);
      setShowSkeleton(false);
    };
  }, []);

  const {
    searchTerm,
    searchResults,
    currentResultIndex,
    setCurrentResultIndex,
    handleSearch,
    navigateResults,
    isLoading,
    scrollToMessage,
  } = useMessageSearch(selectedConversation);

  if (showSkeleton) {
    return (
      <SidebarContainer>
        <Header>
          <CloseButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </CloseButton>
          <Title>Buscar mensagens</Title>
        </Header>
        <Box sx={{ p: 2 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={40}
            sx={{ mb: 2 }}
          />
          {Array.from({ length: 6 }).map((_, index) => (
            <Box
              key={`search-sidebar-skeleton-${index}`}
              sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}
            >
              <Skeleton variant="text" width="80%" height={16} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={14} />
            </Box>
          ))}
        </Box>
      </SidebarContainer>
    );
  }

  if (!isOpen) return null;

  const isMessageFromMe = (message: MessageType) => message.sentBy === "user";
  const hasResults = searchResults.length > 0;

  return (
    <SidebarContainer>
      <Header title="Pesquisar mensagens">
        <CloseButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 20 }} />
        </CloseButton>
      </Header>

      <SearchContainer>
        <SearchInputWrapper>
          <SearchIcon sx={{ color: "#54656f" }} />
          <SearchInput
            id="message-search"
            name="messageSearch"
            placeholder={
              isLoading ? "Carregando mensagens..." : "Pesquisar nesta conversa"
            }
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleSearch(e.target.value)
            }
            fullWidth
            disabled={isLoading}
            inputProps={{
              "aria-label": "Pesquisar mensagens nesta conversa",
            }}
          />
        </SearchInputWrapper>
      </SearchContainer>

      {isLoading && (
        <Box sx={{ p: 2 }}>
          {Array.from({ length: 8 }).map((_, index) => (
            <MessageSkeleton
              key={`search-skeleton-${index}`}
              isFromClient={index % 2 === 0}
            />
          ))}
        </Box>
      )}

      {!isLoading && hasResults && (
        <SearchControls>
          <Tooltip title="Resultado anterior (Shift + Enter)">
            <span>
              <NavigationButton
                onClick={() => navigateResults("prev")}
                disabled={!hasResults}
                size="small"
              >
                <NavigateBeforeIcon />
              </NavigationButton>
            </span>
          </Tooltip>
          <ResultCount>
            {currentResultIndex + 1} de {searchResults.length}
          </ResultCount>
          <Tooltip title="Próximo resultado (Enter)">
            <span>
              <NavigationButton
                onClick={() => navigateResults("next")}
                disabled={!hasResults}
                size="small"
              >
                <NavigateNextIcon />
              </NavigationButton>
            </span>
          </Tooltip>
        </SearchControls>
      )}

      {!isLoading && (
        <MessageList>
          {searchResults.map((message, index) => (
            <MessageItem
              key={message._id}
              id={`search-result-${message._id}`}
              selected={index === currentResultIndex}
              onClick={() => {
                setCurrentResultIndex(index);
                if (message._id) {
                  scrollToMessage(message._id);
                }
              }}
            >
              <MessageText
                dangerouslySetInnerHTML={{
                  __html: message.content?.text || "",
                }}
              />
              <MessageInfo>
                <Typography variant="caption">
                  {formatMessageTime(getMessageTimestamp(message))}
                </Typography>
                {isMessageFromMe(message) && (
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        message.statuses?.[0]?.status === "read"
                          ? "#53bdeb"
                          : "#8696a0",
                    }}
                  >
                    {message.statuses?.[0]?.status === "read" ? "✓✓" : "✓"}
                  </Typography>
                )}
              </MessageInfo>
            </MessageItem>
          ))}
          {searchTerm && !hasResults && (
            <Box sx={{ p: 2, textAlign: "center", color: "#667781" }}>
              Nenhuma mensagem encontrada
            </Box>
          )}
        </MessageList>
      )}
    </SidebarContainer>
  );
};

export default SearchSidebar;
